<template>
  <div class="detail-container">
    <el-button @click="$router.back()">返回订单列表</el-button>
    <el-card v-loading="loading" :loading-text="'加载中...'">
      <!-- 订单基本信息 -->
      <el-descriptions title="订单基本信息" :column="2" border>
        <el-descriptions-item label="订单 ID">{{ order.order_id }}</el-descriptions-item>
        <el-descriptions-item label="订单编号">{{ order.order_number }}</el-descriptions-item>
        <el-descriptions-item label="客户名称">{{ customerName }}</el-descriptions-item>
        <el-descriptions-item label="订单状态">
          <el-tag :type="getStatusType(order.status)">{{ order.status }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="开单时间">{{ formatDateTime(order.order_time) }}</el-descriptions-item>
        <el-descriptions-item label="配送时间">{{ formatDateTime(order.delivery_time) }}</el-descriptions-item>
        <el-descriptions-item label="合计金额" :span="2">
          <span style="font-size: 18px; font-weight: bold; color: #f56c6c;">
            ¥ {{ order.total_amount?.toFixed(2) || '0.00' }}
          </span>
        </el-descriptions-item>
      </el-descriptions>

      <!-- 商品明细 -->
      <div style="margin-top: 30px;">
        <h3>商品明细</h3>
        <el-table :data="order.items" border style="width: 100%">
          <el-table-column type="index" width="50" label="序号"></el-table-column>
          <el-table-column prop="merchandise_name" label="商品名称" min-width="150"></el-table-column>
          <el-table-column prop="specification" label="商品规格" width="120"></el-table-column>
          <el-table-column prop="quantity" label="数量" width="100" align="right">
            <template #default="scope">
              {{ scope.row.quantity }}
            </template>
          </el-table-column>
          <el-table-column prop="price" label="单价" width="120" align="right">
            <template #default="scope">
              ¥ {{ scope.row.price?.toFixed(2) }}
            </template>
          </el-table-column>
          <el-table-column prop="amount" label="金额" width="120" align="right">
            <template #default="scope">
              ¥ {{ scope.row.amount?.toFixed(2) }}
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 操作按钮 -->
      <div style="text-align: right; margin-top: 20px;">
        <el-button type="primary" @click="handlePrint">打印订单</el-button>
        <el-button type="danger" @click="handleDelete">删除订单</el-button>
      </div>
    </el-card>

    <!-- 打印区域（隐藏） -->
    <div v-if="order.order_id" id="printArea" style="display: none;">
      <div class="print-header">
        <h2>销售订单</h2>
        <p>订单编号: {{ order.order_number }}</p>
      </div>
      <div class="print-info">
        <p>客户: {{ customerName }}</p>
        <p>开单时间: {{ formatDateTime(order.order_time) }}</p>
        <p>配送时间: {{ formatDateTime(order.delivery_time) }}</p>
        <p>订单状态: {{ order.status }}</p>
      </div>
      <table class="print-table" border="1" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th>序号</th>
            <th>商品名称</th>
            <th>规格</th>
            <th>数量</th>
            <th>单价</th>
            <th>金额</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in order.items" :key="index">
            <td>{{ index + 1 }}</td>
            <td>{{ item.merchandise_name }}</td>
            <td>{{ item.specification || '-' }}</td>
            <td>{{ item.quantity }}</td>
            <td>{{ item.price?.toFixed(2) }}</td>
            <td>{{ item.amount?.toFixed(2) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="5" style="text-align: right; font-weight: bold;">合计金额:</td>
            <td style="font-weight: bold;">{{ order.total_amount?.toFixed(2) }}</td>
          </tr>
        </tfoot>
      </table>
      <div class="print-footer" style="margin-top: 30px;">
        <p>制单人: ___________</p>
        <p>客户签字: ___________</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import ordersAPI from '@/api/orders'

export default {
  name: 'OrderDetailView',
  props: {
    id: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      order: {
        order_id: null,
        order_number: '',
        customer_id: null,
        customer_name: '',
        order_time: '',
        delivery_time: '',
        status: '',
        total_amount: 0,
        items: []
      },
      loading: false,
      customerName: ''
    }
  },
  created() {
    this.fetchOrderDetail()
  },
  methods: {
    // 获取订单详情
    async fetchOrderDetail() {
      this.loading = true
      try {
        const response = await ordersAPI.inquireOrder(this.id)
        this.order = response

        // 直接使用后端返回的客户名称
        this.customerName = this.order.customer_name || '未知客户'

      } catch (error) {
        console.error('获取订单详情失败:', error)
        ElMessage.error(error.response?.data?.message || '获取订单详情失败')
      } finally {
        this.loading = false
      }
    },

    // 删除订单
    async handleDelete() {
      try {
        await ElMessageBox.confirm(
          '确定要删除该订单吗？删除后数据不可恢复！',
          '删除确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        await ordersAPI.deleteOrder(this.id)
        ElMessage.success('订单删除成功')
        this.$router.push('/orders')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除订单失败:', error)
          ElMessage.error('删除订单失败')
        }
      }
    },

    // 打印订单
    handlePrint() {
      const printContent = document.getElementById('printArea').innerHTML
      const originalTitle = document.title
      document.title = '销售订单-' + this.order.order_number

      const printWindow = window.open('', '_blank')
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${document.title}</title>
            <style>
              body { 
                font-family: 'SimSun', serif; 
                margin: 20px; 
                font-size: 14px;
              }
              .print-header {
                text-align: center;
                margin-bottom: 20px;
                border-bottom: 2px solid #000;
                padding-bottom: 10px;
              }
              .print-header h2 {
                margin: 0;
                font-size: 24px;
              }
              .print-info {
                margin: 20px 0;
              }
              .print-info p {
                margin: 5px 0;
                display: inline-block;
                margin-right: 30px;
              }
              .print-table {
                margin-top: 20px;
                font-size: 14px;
                width: 100%;
                border-collapse: collapse;
              }
              .print-table th,
              .print-table td {
                padding: 8px;
                text-align: center;
                border: 1px solid #000;
              }
              .print-table th {
                background-color: #f5f5f5;
                font-weight: bold;
              }
              .print-footer {
                margin-top: 50px;
              }
              .print-footer p {
                margin: 10px 0;
              }
              @media print {
                body { margin: 0; }
              }
            </style>
          </head>
          <body>${printContent}</body>
        </html>
      `)
      printWindow.document.close()

      printWindow.onload = () => {
        printWindow.focus()
        printWindow.print()
        printWindow.onafterprint = () => {
          printWindow.close()
          document.title = originalTitle
        }
      }
    },

    // 格式化日期时间
    formatDateTime(dateTime) {
      if (!dateTime) return ''
      const date = new Date(dateTime)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/\//g, '-')
    },

    // 获取状态标签类型
    getStatusType(status) {
      const statusMap = {
        '正常': 'primary',
        '已完成': 'success',
        '已取消': 'danger'
      }
      return statusMap[status] || 'info'
    }
  }
}
</script>

<style scoped>
.detail-container {
  padding: 20px;
}

:deep(.el-descriptions__title) {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 20px;
}

:deep(.el-descriptions__label) {
  font-weight: bold;
  background-color: #f5f7fa;
}
</style>